import {
  GCP_BUCKET_STORAGE,
  GCP_PROJECT_ID,
  IDENTITY_PLATFORM_API_KEY,
  IDENTITY_PLATFORM_AUTH_DOMAIN,
  GCP_FIRESTORE_DB_ID,
} from '@/env';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  UserCredential,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  Timestamp,
  collectionGroup,
  query,
  deleteDoc,
} from 'firebase/firestore';
import { FirebaseError, initializeApp } from 'firebase/app';
import { Clinic, Invitation, Patient, Test, User } from '@/types/database';
import { getTranslatedErrorMessage } from './helpers';
import { getStorage, ref } from 'firebase/storage';

import FireCRUD from './FireCRUD';

const firebaseConfig = {
  apiKey: IDENTITY_PLATFORM_API_KEY,
  authDomain: IDENTITY_PLATFORM_AUTH_DOMAIN,
  projectId: GCP_PROJECT_ID,
  storageBucket: GCP_BUCKET_STORAGE,
};

const app = initializeApp(firebaseConfig);

const db = getFirestore(GCP_FIRESTORE_DB_ID);
const storageBucket = getStorage();
const auth = getAuth();

const clinicsRef = collection(db, 'clinics');
const testCollectionRef = collection(db, 'tests');

export const userCrud = new FireCRUD<User>('users', db);
export const clinicCrud = new FireCRUD<Clinic>('clinics', db);
export const invitationCrud = new FireCRUD<Invitation>('invitations', db);
export const testCrud = new FireCRUD<Test>('tests', db);
export const patientCrud = new FireCRUD<Patient>('patients', db);

export const createUser = async (userDetails: Partial<User> & { password: string }) => {
  const email = userDetails.email as string;
  let newUser = (await userCrud.read([['email', '==', email]])) as User;
  const doesUserExist = !!newUser;
  const { password, ...user } = userDetails;
  if (doesUserExist) {
    userCrud.update(newUser.id, userDetails);
  } else {
    const  authUser = await createUserWithEmailAndPassword(auth, email, password).catch(handleFirebaseError);
    newUser = await userCrud.create(user, authUser.user.uid);
    await signOut(auth);
  }

  return newUser;
};

export const signinAuthUser = (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email, password).catch(handleFirebaseError);
};

export const requestAuthUserPasswordReset = async (email: string) => {
  return sendPasswordResetEmail(auth, email).catch(handleFirebaseError);
};

export const signoutAuthUser = async () => {
  return signOut(auth).catch(handleFirebaseError);
};

export const onAuthStateChanged = (cb: () => void) => {
  return auth.onAuthStateChanged(cb, handleFirebaseError);
};

export const getFileRef = (filePath: string) => {
  return ref(storageBucket, filePath);
};

const handleFirebaseError = (err: FirebaseError | Error) => {
  if (err instanceof FirebaseError) {
    throw getTranslatedErrorMessage(err);
  } else {
    console.error('Unknown error:', err);
  }

  throw err as FirebaseError;
};

export const createPatientinClinic = async (patient: Partial<Patient>, clinicId: string) => {
  const collectionRef = collection(clinicsRef, clinicId, 'patients');
  const docRef = doc(collectionRef);
  const now = Timestamp.now();

  const patientData: Patient = {
    name: patient.name as string,
    email: patient.email as string,
    phone: patient.phone,
    createdAt: now,
    tests: [],
    id: docRef.id,
    clinicId,
  };

  await setDoc(docRef, patientData, { merge: true }).catch(handleFirebaseError);

  return patientData;
};

export default app;
export { db, storageBucket, auth };
